import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, ExternalLink, HandCoins } from 'lucide-react';

import { useIntegrationsList } from '@/lib/api/integrations';

import { WizardOptionCard } from './WizardOptionCard';
import type { PaymentMode, QuickstartAnswers } from './types';

interface StepPaymentMethodProps {
    answers: QuickstartAnswers;
    onChange: (next: Partial<QuickstartAnswers>) => void;
    storeId: string;
    onConnectIntegration: (integrationId: string) => void;
}

const MANUAL_INTEGRATION_TYPES = ['zelle', 'custom_payment'] as const;

/**
 * Returns the manual payment integration types this store has configured.
 * The wizard uses this both to gate Q4=manual and to know which methods to
 * write to the checkout step's paymentSettings overlay.
 */
function useAvailableManualMethods(storeId: string): string[] {
    const { data: integrations } = useIntegrationsList(storeId);
    return React.useMemo(() => {
        const list = integrations ?? [];
        return MANUAL_INTEGRATION_TYPES.filter((provider) =>
            list.some((i) => i.provider === provider && i.enabled),
        );
    }, [integrations]);
}

export function StepPaymentMethod({
    answers,
    onChange,
    storeId,
    onConnectIntegration,
}: StepPaymentMethodProps) {
    const { t } = useTranslation('common');
    const availableManual = useAvailableManualMethods(storeId);
    const manualAvailable = availableManual.length > 0;

    const select = (value: PaymentMode) => {
        onChange({
            paymentMode: value,
            manualPaymentMethods: value === 'manual' ? availableManual : [],
        });
    };

    // Preselect the first option ("Standard") on mount.
    React.useEffect(() => {
        if (answers.paymentMode === null) {
            onChange({ paymentMode: 'standard', manualPaymentMethods: [] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep manualPaymentMethods in sync if availability changes mid-session.
    React.useEffect(() => {
        if (answers.paymentMode === 'manual') {
            const sameSet = availableManual.length === answers.manualPaymentMethods.length
                && availableManual.every((m) => answers.manualPaymentMethods.includes(m));
            if (!sameSet) {
                onChange({ manualPaymentMethods: availableManual });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableManual, answers.paymentMode]);

    const renderManualDisabledReason = () => (
        <div className="flex items-center gap-2">
            <span>{t('funnelQuickstart.paymentMethod.manual.noIntegration')}</span>
            <span
                role="link"
                tabIndex={0}
                onClick={(e) => {
                    e.stopPropagation();
                    onConnectIntegration('zelle');
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        onConnectIntegration('zelle');
                    }
                }}
                className="text-primary hover:text-primary/80 inline-flex cursor-pointer items-center gap-1 font-medium underline-offset-2 hover:underline"
            >
                {t('funnelQuickstart.paymentMethod.manual.setUpNow')}
                <ExternalLink className="h-3 w-3" />
            </span>
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h3 className="text-lg font-semibold">
                    {t('funnelQuickstart.paymentMethod.title')}
                </h3>
                <p className="text-muted-foreground text-sm">
                    {t('funnelQuickstart.paymentMethod.subtitle')}
                </p>
            </div>

            <div className="flex flex-col gap-3">
                <WizardOptionCard
                    title={t('funnelQuickstart.paymentMethod.standard.title')}
                    description={t('funnelQuickstart.paymentMethod.standard.description')}
                    icon={<CreditCard className="h-5 w-5" />}
                    selected={answers.paymentMode === 'standard'}
                    onSelect={() => select('standard')}
                />
                <WizardOptionCard
                    title={t('funnelQuickstart.paymentMethod.manual.title')}
                    description={t('funnelQuickstart.paymentMethod.manual.description')}
                    icon={<HandCoins className="h-5 w-5" />}
                    selected={answers.paymentMode === 'manual'}
                    disabled={!manualAvailable}
                    disabledReason={!manualAvailable ? renderManualDisabledReason() : undefined}
                    onSelect={() => manualAvailable && select('manual')}
                />
            </div>

            {answers.paymentMode === 'manual' && availableManual.length > 0 && (
                <p className="text-muted-foreground text-xs">
                    {t('funnelQuickstart.paymentMethod.manual.selectedMethods', {
                        methods: availableManual.join(', '),
                    })}
                </p>
            )}
        </div>
    );
}
